/**
 * Monthly Fink Family themes. Colors are applied as CSS variables on <html>
 * so existing slate/violet utilities pick them up.
 */

export type SeasonId =
  | 'new-year'
  | 'valentine'
  | 'lucky'
  | 'spring'
  | 'flowers'
  | 'summer'
  | 'stars'
  | 'golden'
  | 'early-fall'
  | 'spooky'
  | 'thanksgiving'
  | 'christmas';

export type Motif =
  | 'snow'
  | 'hearts'
  | 'shamrocks'
  | 'petals'
  | 'bubbles'
  | 'stars'
  | 'fireflies'
  | 'leaves'
  | 'bats';

export interface Season {
  id: SeasonId;
  month: number;
  label: string;
  motif: Motif;
  /** How many drifting shapes to draw on a wide screen. */
  count: number;
  vars: Record<string, string>;
}

interface PaletteInput {
  s950: string;
  s900: string;
  s800: string;
  s700: string;
  s600: string;
  s500: string;
  s400: string;
  s300: string;
  s200: string;
  s100: string;
  v950: string;
  /** Primary buttons. Must stay dark enough for white text. */
  v600: string;
  v500: string;
  v400: string;
  /** Clock and season chip. */
  v300: string;
  paper: string;
}

function palette(p: PaletteInput): Record<string, string> {
  return {
    '--color-slate-950': p.s950,
    '--color-slate-900': p.s900,
    '--color-slate-800': p.s800,
    '--color-slate-700': p.s700,
    '--color-slate-600': p.s600,
    '--color-slate-500': p.s500,
    '--color-slate-400': p.s400,
    '--color-slate-300': p.s300,
    '--color-slate-200': p.s200,
    '--color-slate-100': p.s100,
    '--color-violet-950': p.v950,
    '--color-violet-600': p.v600,
    '--color-violet-500': p.v500,
    '--color-violet-400': p.v400,
    '--color-violet-300': p.v300,
    '--color-accent': p.v600,
    '--color-accent-soft': p.v300,
    '--season-paper': p.paper,
  };
}

export const SEASONS: readonly Season[] = [
  {
    id: 'new-year',
    month: 1,
    label: 'New Year',
    motif: 'snow',
    count: 18,
    vars: palette({
      s950: '#071018',
      s900: '#102033',
      s800: '#18324c',
      s700: '#234868',
      s600: '#3d6d90',
      s500: '#5c8aab',
      s400: '#c5dff0',
      s300: '#dceef8',
      s200: '#eef6fb',
      s100: '#f7fbfe',
      v950: '#082f49',
      v600: '#0369a1',
      v500: '#0284c7',
      v400: '#f5d78e',
      v300: '#fde68a',
      paper: '#f3f7fb',
    }),
  },
  {
    id: 'valentine',
    month: 2,
    label: 'Valentine',
    motif: 'hearts',
    count: 14,
    vars: palette({
      s950: '#1c0a12',
      s900: '#31101e',
      s800: '#4a182e',
      s700: '#6b2444',
      s600: '#8f3a60',
      s500: '#b05a7c',
      s400: '#f0c2d4',
      s300: '#f7d5e3',
      s200: '#fbe8ef',
      s100: '#fdf5f8',
      v950: '#4c0519',
      v600: '#be123c',
      v500: '#e11d48',
      v400: '#fb7185',
      v300: '#fecdd3',
      paper: '#fdf2f5',
    }),
  },
  {
    id: 'lucky',
    month: 3,
    label: 'Lucky',
    motif: 'shamrocks',
    count: 12,
    vars: palette({
      s950: '#07140e',
      s900: '#0f2618',
      s800: '#173824',
      s700: '#215234',
      s600: '#34724a',
      s500: '#4f9468',
      s400: '#bfe8cc',
      s300: '#d7f3e0',
      s200: '#eaf9ef',
      s100: '#f4fcf6',
      v950: '#052e16',
      v600: '#166534',
      v500: '#15803d',
      v400: '#facc15',
      v300: '#fde047',
      paper: '#f3faf5',
    }),
  },
  {
    id: 'spring',
    month: 4,
    label: 'Spring',
    motif: 'petals',
    count: 16,
    vars: palette({
      s950: '#07131c',
      s900: '#102433',
      s800: '#18384e',
      s700: '#245270',
      s600: '#3c789c',
      s500: '#5a96b8',
      s400: '#c9e6f6',
      s300: '#def0fa',
      s200: '#f0f8fc',
      s100: '#f7fbfe',
      v950: '#500724',
      v600: '#be185d',
      v500: '#db2777',
      v400: '#f9a8d4',
      v300: '#fbcfe8',
      paper: '#f8f5f8',
    }),
  },
  {
    id: 'flowers',
    month: 5,
    label: 'Flowers',
    motif: 'petals',
    count: 14,
    vars: palette({
      s950: '#160c16',
      s900: '#281428',
      s800: '#3c2040',
      s700: '#58305c',
      s600: '#7a4a80',
      s500: '#9a70a0',
      s400: '#ecc8ee',
      s300: '#f4def6',
      s200: '#faeefb',
      s100: '#fdf7fd',
      v950: '#4a044e',
      v600: '#a21caf',
      v500: '#c026d3',
      v400: '#f0abfc',
      v300: '#f5d0fe',
      paper: '#fbf7fb',
    }),
  },
  {
    id: 'summer',
    month: 6,
    label: 'Summer',
    motif: 'bubbles',
    count: 14,
    vars: palette({
      s950: '#041416',
      s900: '#0a2628',
      s800: '#103a3e',
      s700: '#16545a',
      s600: '#247880',
      s500: '#3d9aa2',
      s400: '#b6eef2',
      s300: '#d2f6f8',
      s200: '#e9fbfc',
      s100: '#f4fdfd',
      v950: '#042f2e',
      v600: '#0f766e',
      v500: '#0d9488',
      v400: '#facc15',
      v300: '#fde047',
      paper: '#f2fbfb',
    }),
  },
  {
    id: 'stars',
    month: 7,
    label: 'Stars',
    motif: 'stars',
    count: 18,
    vars: palette({
      s950: '#070b16',
      s900: '#10182c',
      s800: '#182444',
      s700: '#243666',
      s600: '#3c5690',
      s500: '#5c78ae',
      s400: '#c9d6f4',
      s300: '#dde6f8',
      s200: '#eef2fb',
      s100: '#f6f8fd',
      v950: '#172554',
      v600: '#b91c1c',
      v500: '#dc2626',
      v400: '#fecaca',
      v300: '#fee2e2',
      paper: '#f6f7fb',
    }),
  },
  {
    id: 'golden',
    month: 8,
    label: 'Golden Hour',
    motif: 'fireflies',
    count: 12,
    vars: palette({
      s950: '#161008',
      s900: '#2a1c0e',
      s800: '#402a14',
      s700: '#5e3e1c',
      s600: '#866032',
      s500: '#a67c4a',
      s400: '#f0d2ae',
      s300: '#f7e4cc',
      s200: '#fbf1e4',
      s100: '#fdf8f3',
      v950: '#451a03',
      v600: '#9a3412',
      v500: '#c2410c',
      v400: '#fbbf24',
      v300: '#fde68a',
      paper: '#fdf8f1',
    }),
  },
  {
    id: 'early-fall',
    month: 9,
    label: 'Early Fall',
    motif: 'leaves',
    count: 8,
    vars: palette({
      s950: '#0c1018',
      s900: '#161c2e',
      s800: '#222a44',
      s700: '#323e64',
      s600: '#4c5e8c',
      s500: '#6c7eaa',
      s400: '#cdd6ee',
      s300: '#e0e7f6',
      s200: '#eef2fb',
      s100: '#f7f8fc',
      v950: '#450a0a',
      v600: '#b91c1c',
      v500: '#dc2626',
      v400: '#fb923c',
      v300: '#fed7aa',
      paper: '#f7f6f4',
    }),
  },
  {
    id: 'spooky',
    month: 10,
    label: 'Spooky',
    motif: 'bats',
    count: 12,
    vars: palette({
      s950: '#140818',
      s900: '#22102e',
      s800: '#321848',
      s700: '#4a2866',
      s600: '#6b4588',
      s500: '#8d6aa6',
      s400: '#cbb6dc',
      s300: '#e4d6f0',
      s200: '#f1e9f7',
      s100: '#f8f4fb',
      v950: '#3b0764',
      v600: '#c2410c',
      v500: '#ea580c',
      v400: '#fdba74',
      v300: '#ffedd5',
      paper: '#f7f1ea',
    }),
  },
  {
    id: 'thanksgiving',
    month: 11,
    label: 'Thanksgiving',
    motif: 'leaves',
    count: 16,
    vars: palette({
      s950: '#1a1008',
      s900: '#2c1a0e',
      s800: '#422616',
      s700: '#63381c',
      s600: '#8a5630',
      s500: '#a87448',
      s400: '#e4c4a4',
      s300: '#f0dcc8',
      s200: '#f7ebdf',
      s100: '#fbf6f0',
      v950: '#431407',
      v600: '#9a3412',
      v500: '#c2410c',
      v400: '#fbbf24',
      v300: '#fde68a',
      paper: '#fbf6ef',
    }),
  },
  {
    id: 'christmas',
    month: 12,
    label: 'Christmas',
    motif: 'snow',
    count: 18,
    vars: palette({
      s950: '#071510',
      s900: '#0e261c',
      s800: '#163828',
      s700: '#1f5238',
      s600: '#317454',
      s500: '#4e9470',
      s400: '#b7e0c8',
      s300: '#d4f0e0',
      s200: '#e8f7ef',
      s100: '#f4fbf7',
      v950: '#450a0a',
      v600: '#b91c1c',
      v500: '#dc2626',
      v400: '#f5d76e',
      v300: '#fde68a',
      paper: '#f6fbf7',
    }),
  },
];

export function seasonForMonth(month: number): Season {
  const found = SEASONS.find((s) => s.month === month);
  if (!found) {
    throw new Error(`No season for month ${month}`);
  }
  return found;
}

/** `?month=1`..`12` previews a season and is not stored. */
export function monthOverride(search: string): number | null {
  const raw = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search).get(
    'month'
  );
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  if (!Number.isInteger(n) || n < 1 || n > 12) return null;
  return n;
}

export function resolveSeason(date: Date, search = ''): Season {
  const month = monthOverride(search) ?? date.getMonth() + 1;
  return seasonForMonth(month);
}

export function applySeason(root: HTMLElement, season: Season): void {
  root.dataset.season = season.id;
  for (const [key, value] of Object.entries(season.vars)) {
    root.style.setProperty(key, value);
  }
}
